import { AuthProvider } from "react-admin";
import { TokenStorage } from "./TokenStorage";
import type { TokenPair } from "./TokenStorage";
import { tracedHttpClient } from "../api/TracedHttpClient";
import { browserFetch } from "../api/BrowserFetch";
import type { Permission } from "./HasPermission";

interface CurrentUser {
  id: string;
  name: string;
  role: { permissions: Permission[]; level: number; type: "system" | "user" };
}

async function fetchCurrentUser(): Promise<CurrentUser> {
  const response = await tracedHttpClient("/api/me");
  return response.json as CurrentUser;
}

// Ошибка, о которой react-admin спрашивает провайдера: код состояния берётся из
// ответа сервера, когда ответ вообще был.
interface CheckedError {
  // Отсутствует, когда до ответа сервера дело не дошло: сетевой сбой или
  // прерывание запроса кода состояния не несут.
  status?: number;
}

export const authProvider: AuthProvider = {
  async login(credentials) {
    const { username, password } = credentials as { username: string; password: string };
    const response = await browserFetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ login: username, password }),
    });
    if (!response.ok) {
      throw new Error("Неверный логин или пароль");
    }
    const tokens = (await response.json()) as TokenPair;
    TokenStorage.setTokens(tokens);
  },

  logout() {
    TokenStorage.clearTokens();
    return Promise.resolve();
  },

  checkAuth() {
    // react-admin читает исход проверки как промис: отсутствие токена — отказ,
    // иначе сессия признана живой.
    const authorized = TokenStorage.getAccessToken() !== null;
    return authorized ? Promise.resolve() : Promise.reject(new Error("Сессия не найдена"));
  },

  checkError(error: CheckedError) {
    let checkOutcome: Promise<void>;
    if (error.status === 419) {
      TokenStorage.clearTokens();
      checkOutcome = Promise.reject(new Error("Сессия истекла"));
    } else {
      checkOutcome = Promise.resolve();
    }
    return checkOutcome;
  },

  async getIdentity() {
    const user = await fetchCurrentUser();
    return { id: user.id, fullName: user.name, level: user.role.level, roleType: user.role.type };
  },

  async getPermissions() {
    const user = await fetchCurrentUser();
    return user.role.permissions;
  },
};
